'use client';

import { ReactNode, useEffect, useRef, useState } from 'react';

interface Props {
  /** Small uppercase label rendered above the title (e.g. "CLIENT"). */
  eyebrow: string;
  /** Bold title row — falsy → only the eyebrow renders. */
  title?: string;
  /**
   * Distance from the viewport top in pixels. Owned by the parent so it can
   * keep both the desktop rail and this capsule aligned to the same
   * below-the-header anchor.
   */
  topOffsetPx: number;
  /** Full sidebar content, revealed inside the disclosure panel on tap. */
  children: ReactNode;
}

/**
 * Collapsible mobile disclosure for the CaseStudyChallenge sidebar. Mirrors
 * `BlogTocMobile`: a fixed-position pill at the top of the viewport with a
 * chevron; tapping it opens a scrollable panel with the full aside content
 * and outside clicks close it back.
 *
 * The parent (`StickyChallengeSidebar`) portals this into `document.body` so
 * it can outlive its slice's containing block; the `md:hidden` class here is
 * defence-in-depth against the desktop rail ever bleeding onto mobile.
 *
 * The parent conditionally renders this component (mount/unmount instead of
 * visibility toggling), so the `open` state naturally resets to `false` every
 * time the capsule reappears — no reset-effect required.
 */
export default function MobileChallengeCapsule({
  eyebrow,
  title,
  topOffsetPx,
  children,
}: Props) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onDocPointerDown = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node | null;
      if (target && rootRef.current && !rootRef.current.contains(target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocPointerDown);
    document.addEventListener('touchstart', onDocPointerDown, {
      passive: true,
    });
    return () => {
      document.removeEventListener('mousedown', onDocPointerDown);
      document.removeEventListener('touchstart', onDocPointerDown);
    };
  }, [open]);

  return (
    <div
      ref={rootRef}
      className="fixed inset-x-4 z-30 md:hidden"
      style={{ top: topOffsetPx }}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 rounded-[8px] bg-[#f4f4f4] px-[15px] py-3 text-left shadow-[0_4px_12px_rgba(0,0,0,0.08)]"
      >
        <span className="flex min-w-0 flex-col">
          <span className="font-inter text-[11px] font-medium uppercase leading-[16px] tracking-wide text-[#4B0FEA]">
            {eyebrow}
          </span>
          {title && (
            <span className="truncate font-inter text-[14px] font-bold leading-[20px] text-[#041118]">
              {title}
            </span>
          )}
        </span>
        <svg
          className={`shrink-0 transition-transform duration-200 ${
            open ? 'rotate-180' : ''
          }`}
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden
        >
          <path
            d="M6 9l6 6 6-6"
            stroke="#041118"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        <div className="mt-2 max-h-[70vh] overflow-auto rounded-[8px] bg-white shadow-[0_12px_24px_rgba(0,0,0,0.15)]">
          {children}
        </div>
      )}
    </div>
  );
}
